import { ToggleOption_CheckoutConfigOptionGroupFragment } from "src/fragments/circularFragments.generated";
import { Checkbox, DebugText, LoadingBone, OptionPreviewImage, OptionPreviewImageLoader } from "~components";
import { Css } from "~generated/css";
import { formatDeltaPriceDisplay, queryResult } from "~utils";
import {
  ComparePackageOptions_CheckoutConfigOptionFragment,
  useComparePackageOptions_LoadQuery,
} from "./ComparePackageOptions.generated";

type ComparePackageOptionsContentProps = {
  checkoutConfigId: string;
  rpogId: string;
  ccog: ToggleOption_CheckoutConfigOptionGroupFragment;
  selectedOptionIds: string[];
  onSelect: (selectedOptionId: string) => void;
};

export function ComparePackageOptionsContent(props: ComparePackageOptionsContentProps) {
  const { checkoutConfigId, rpogId, ccog, selectedOptionIds, onSelect } = props;

  const query = useComparePackageOptions_LoadQuery({
    variables: { checkoutConfigId, rpogId },
  });

  return queryResult(query, {
    data: ({ checkoutConfig: { optionGroups } }) => {
      // Since we're filtering by rpogId, we should get exactly one option group, should *never* happen but since we're technically receiving
      // an array throw a warning.
      if (optionGroups.length > 1) {
        console.warn(`Expected 1 option group, got ${optionGroups.length} for rpogId: ${rpogId}`);
      }

      // Create order map from parent's already-sorted options
      // and then sort the query results to match the stable order from parent to prevent reordering when selections change
      const orderMap = new Map(ccog.options.map((option, index) => [option.id, index]));
      const sortedOptions = [...optionGroups[0].options].sort((a, b) => {
        const aIndex = orderMap.get(a.option.id) ?? 0;
        const bIndex = orderMap.get(b.option.id) ?? 0;
        return aIndex - bIndex;
      });

      return (
        <ComparePackageOptionsContentView
          options={sortedOptions}
          selectedOptionIds={selectedOptionIds}
          onSelect={onSelect}
        />
      );
    },
    loading: () => <SkeletonComparePackageContent />,
  });
}

type ComparePackageOptionsContentViewProps = {
  options: ComparePackageOptions_CheckoutConfigOptionFragment[];
  // We technically could use the isSelected field from the option fragment, but selectedOptionIds gives
  // users instant feedback
  selectedOptionIds: string[];
  onSelect: (selectedOptionId: string) => void;
};

function ComparePackageOptionsContentView(props: ComparePackageOptionsContentViewProps) {
  const { options, selectedOptionIds, onSelect } = props;

  // Calculate the maximum number of products across all options to set the grid rows
  const maxProducts = Math.max(...options.map((o) => o.packageOptionMaterials.length), 0);

  return (
    <div
      css={{
        // Center our rows/columns but don't grow beyond the viewport width
        ...Css.maxh100.px3.mxa.maxw100.bgWhite.oa.$,
        // Parent grid - columns for each option, rows auto-generated and maintain equal height
        ...Css.relative.pb3.dg
          .colGap(6)
          .gtc(`repeat(${options.length}, 362px)`)
          .gtr(`auto repeat(${maxProducts}, auto)`).$,
      }}
    >
      {/* Each option col is a subgrid that inherits parent rows */}
      {options.map((option) => {
        const { option: optionFragment, totalPriceInCents, packageOptionMaterials, id: optionId } = option;
        const isSelected = selectedOptionIds.includes(optionFragment.id);

        return (
          <div key={optionFragment.id} css={Css.dg.gtr("subgrid").gr(`1 / span ${maxProducts + 1}`).$}>
            {/* Option header with price and selection */}
            <div css={Css.df.jcsb.gap1.aifs.pb1.pt3.z1.sticky.top0.bgWhite.$}>
              <DebugText>{optionFragment.id}</DebugText>
              <div css={Css.t18.$}>{optionFragment.name}</div>
              <div css={Css.df.gap1.aic.$}>
                <div css={Css.t12.wsnw.$}>{formatDeltaPriceDisplay(totalPriceInCents)}</div>
                <Checkbox onClick={() => onSelect(optionFragment.id)} selected={isSelected} />
              </div>
            </div>

            {/* Option product rows */}
            {packageOptionMaterials.map((packageOptionMaterial, productIndex) => {
              const { featuredImage, displayName, modelNumber, listing, id: mvId } = packageOptionMaterial;
              const debugText = `optionId: ${optionId} - packageOptionMaterialId: ${mvId}`;

              return (
                <div
                  css={
                    Css.df.gap2.py3.z0
                      .if(productIndex < packageOptionMaterials.length - 1)
                      .add("borderBottom", "1px solid rgba(53, 53, 53, 0.16)").$
                  }
                  key={`${optionId}-${mvId}`}
                >
                  <OptionPreviewImage
                    optionName={displayName}
                    imageUrl={featuredImage?.asset?.thumbnail155}
                    size={96}
                  />
                  <div css={Css.df.fdc.gap1.$}>
                    <DebugText>{debugText}</DebugText>
                    <div>
                      <div css={Css.t10.gray.$}>{listing?.brand?.name}</div>
                      <div css={Css.t12.$}>{displayName}</div>
                    </div>
                    <div css={Css.df.fdc.t10.gray.$}>Model: {modelNumber ?? "-"}</div>
                    <div css={Css.df.fdc.t10.gray.lineClamp4.$}>{listing?.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function SkeletonComparePackageContent() {
  return (
    <div
      css={{
        ...Css.maxh100.px3.mxa.maxw100.bgWhite.oa.$,
        ...Css.relative.pb3.dg.colGap(6).gtc("repeat(4, 362px)").gtr("auto repeat(5, auto)").$,
      }}
    >
      <SkeletonColumn />
      <SkeletonColumn />
      <SkeletonColumn />
      <SkeletonColumn />
    </div>
  );
}

// Show 4 columns with 5 product rows each as a reasonable skeleton
function SkeletonProductRow({ hideBorder }: { hideBorder?: boolean }) {
  return (
    <div css={Css.df.gap2.py3.z0.if(!hideBorder).add("borderBottom", "1px solid rgba(53, 53, 53, 0.16)").$}>
      <OptionPreviewImageLoader size={96} />
      <div css={Css.df.fdc.gap1.$}>
        <div css={Css.df.fdc.gapPx(4).$}>
          <div css={Css.t10.gray.$}>
            <LoadingBone width="80px" height="10px" />
          </div>
          <div css={Css.t12.$}>
            <LoadingBone width="140px" height="12px" />
          </div>
        </div>
        <div css={Css.df.fdc.t10.gray.$}>
          <LoadingBone width="100px" height="10px" />
        </div>
        <div css={Css.df.fdc.gapPx(4).t10.gray.$}>
          <LoadingBone width="200px" height="10px" />
          <LoadingBone width="180px" height="10px" />
          <LoadingBone width="190px" height="10px" />
        </div>
      </div>
    </div>
  );
}

function SkeletonColumn() {
  return (
    <div css={Css.dg.gtr("subgrid").gr("1 / span 6").$}>
      {/* Option header skeleton */}
      <div css={Css.df.jcsb.gap1.aifs.pb1.pt3.z1.sticky.top0.bgWhite.$}>
        <LoadingBone width="120px" height="18px" />
        <div css={Css.df.gap1.aic.$}>
          <LoadingBone width="58px" height="12px" />
          <Checkbox selected={false} disabled />
        </div>
      </div>
      {/* Product rows skeleton */}
      <SkeletonProductRow />
      <SkeletonProductRow />
      <SkeletonProductRow />
      <SkeletonProductRow />
      <SkeletonProductRow hideBorder />
    </div>
  );
}
